import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LogInIcon, MailIcon, ShieldCheckIcon, ArrowLeftIcon, TicketIcon, UserPlusIcon } from 'lucide-react';
import { LoadingScreen } from '../../../shared/ui/LoadingScreen';
import { useAuth } from '../../../app/providers/AuthProvider';
import { theme, getTenant } from '../../../theme/tokens';
import { BRAND } from '../../../shared/config/brand';
// Brand flagship branch — splash hero (CIMB Bank tower)
const HERO_IMAGE = BRAND.heroImageUrl;
// Theme color tokens for neutral UI; brand palette + identity come from the
// single source of truth in shared/config/brand.ts (the generated theme layer
// is regenerated and unreliable for brand values).
const {
  colors
} = theme;
const brand = BRAND.colors;
const CIMB_LOGO = BRAND.logoUrl;
const CIMB_NAME = BRAND.name;
type Step = 'login' | 'mfa' | 'invite-code' | 'invite-setup' | 'loading';
export function Login() {
  const {
    t
  } = useTranslation('auth');
  const navigate = useNavigate();
  const {
    login
  } = useAuth();
  const [step, setStep] = useState<Step>('login');
  const [email, setEmail] = useState('simon.verraest@bilabila.co');
  const [mfaCode, setMfaCode] = useState(['', '', '', '', '', '']);
  const [resendCountdown, setResendCountdown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  // Invitation flow state
  const [inviteCode, setInviteCode] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [invitePassword, setInvitePassword] = useState('');
  // Countdown timer for resend
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);
  // Auto-focus first MFA input when entering MFA step
  useEffect(() => {
    if (step === 'mfa') {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [step]);
  const getMaskedEmail = () => {
    const [local, domain] = email.split('@');
    if (!domain) return email;
    const masked = local.charAt(0) + '•••' + (local.length > 1 ? local.charAt(local.length - 1) : '');
    return `${masked}@${domain}`;
  };
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('mfa');
    setResendCountdown(30);
  };
  const handleMfaSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('loading');
    setTimeout(() => {
      login();
      navigate('/overview');
    }, 2000);
  };
  const handleMfaInput = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newCode = [...mfaCode];
      digits.forEach((digit, i) => {
        if (index + i < 6) newCode[index + i] = digit;
      });
      setMfaCode(newCode);
      const nextIndex = Math.min(index + digits.length, 5);
      inputRefs.current[nextIndex]?.focus();
      return;
    }
    if (!/^\d*$/.test(value)) return;
    const newCode = [...mfaCode];
    newCode[index] = value;
    setMfaCode(newCode);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };
  const handleMfaKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !mfaCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      const newCode = [...mfaCode];
      newCode[index - 1] = '';
      setMfaCode(newCode);
    }
  };
  const handleResend = () => {
    if (resendCountdown > 0) return;
    setMfaCode(['', '', '', '', '', '']);
    setResendCountdown(30);
    inputRefs.current[0]?.focus();
  };
  const isCodeComplete = mfaCode.every(digit => digit !== '');
  // Invitation flow handlers
  const handleInviteCodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setInviteError('');
    const normalized = inviteCode.trim();
    // Mock validation: any non-empty code passes; codes literally "INVALID" fail
    if (!normalized || normalized.toUpperCase() === 'INVALID') {
      setInviteError(t('pages.Login.invite_code_invalid'));
      return;
    }
    // Prefill the email field (mock — would come from invitation record)
    setInviteEmail('invited.user@bilabila.my');
    setStep('invite-setup');
  };
  const handleInviteSetupSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('loading');
    setTimeout(() => {
      login();
      navigate('/overview');
    }, 2000);
  };
  const resetInvite = () => {
    setInviteCode('');
    setInviteError('');
    setInviteEmail('');
    setInviteFirstName('');
    setInviteLastName('');
    setInvitePassword('');
  };
  const isInviteSetupValid = inviteFirstName.trim().length > 0 && inviteLastName.trim().length > 0 && invitePassword.length >= 8;
  if (step === 'loading') {
    return <LoadingScreen data-id="element-2692" />;
  }
  const inputBaseStyle: React.CSSProperties = {
    border: `1px solid ${colors.border.DEFAULT}`,
    backgroundColor: colors.neutral[50]
  };
  return <div className="min-h-screen flex bg-white" data-id="element-2693">
      {/* Left Side - Image & Branding (Hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 relative" style={{
      backgroundColor: colors.background.hero
    }} data-id="element-2694">
        <img src={HERO_IMAGE} alt={CIMB_NAME} className="absolute inset-0 w-full h-full object-cover" data-id="element-2695" />

        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/85 via-gray-900/30 to-transparent" data-id="element-2696" />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full" data-id="element-2697">
          <div data-id="element-2698">
            <img src={CIMB_LOGO} alt={CIMB_NAME} className="h-12 w-auto object-contain" data-id="element-2699" />
          </div>
          <div className="max-w-xl" data-id="element-2700">
            <h1 className="text-4xl font-bold mb-4 leading-tight" style={{
            color: colors.text.inverse
          }} data-id="element-2701">
              {t('pages.Login.powering_singapores_digital_commerce')}
            </h1>
            <p className="text-lg text-gray-200" data-id="element-2702">
              {t('pages.Login.the_complete_payment_solution_for_modern')}
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form / MFA / Invitation */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center px-6 py-12 sm:px-12" data-id="element-2703">
        <div className="w-full" style={{
        maxWidth: theme.layout.auth.maxWidth
      }} data-id="element-2704">
          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-8" data-id="element-2705">
            <img src={CIMB_LOGO} alt={CIMB_NAME} className="h-10 w-auto object-contain" data-id="element-2706" />
          </div>

          {step === 'login' && <>
              <div className="mb-10 text-center lg:text-left" data-id="element-2707">
                <h2 className="text-3xl font-bold mb-2" style={{
              color: colors.text.primary
            }} data-id="element-2708">
                  {t('pages.Login.merchant_portal')}
                </h2>
                <p style={{
              color: colors.text.secondary
            }} data-id="element-2709">
                  {t('pages.Login.sign_in_to_manage_your_business')}
                </p>
              </div>

              <form onSubmit={handleLogin} className="space-y-6" data-id="element-2710">
                <div data-id="element-2711">
                  <label className="block text-sm font-medium mb-2" style={{
                color: colors.neutral[700]
              }} data-id="element-2712">
                    {t('pages.Login.email_address')}
                  </label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('pages.Login.attrs.simonverraestsiamtradingth')} className="w-full px-4 py-3 rounded-lg outline-none transition-colors" style={inputBaseStyle} onFocus={e => {
                e.target.style.borderColor = brand.primary;
                e.target.style.boxShadow = `0 0 0 2px ${brand.primaryRing}`;
                e.target.style.backgroundColor = colors.neutral[0];
              }} onBlur={e => {
                e.target.style.borderColor = colors.border.DEFAULT;
                e.target.style.boxShadow = 'none';
                e.target.style.backgroundColor = colors.neutral[50];
              }} required data-id="element-2713" />
                </div>

                <div data-id="element-2714">
                  <label className="block text-sm font-medium mb-2" style={{
                color: colors.neutral[700]
              }} data-id="element-2715">
                    {t('pages.Login.password')}
                  </label>
                  <input type="password" defaultValue="••••••••" placeholder="••••••••" className="w-full px-4 py-3 rounded-lg outline-none transition-colors" style={inputBaseStyle} onFocus={e => {
                e.target.style.borderColor = brand.primary;
                e.target.style.boxShadow = `0 0 0 2px ${brand.primaryRing}`;
                e.target.style.backgroundColor = colors.neutral[0];
              }} onBlur={e => {
                e.target.style.borderColor = colors.border.DEFAULT;
                e.target.style.boxShadow = 'none';
                e.target.style.backgroundColor = colors.neutral[50];
              }} required data-id="element-2716" />
                </div>

                <div className="flex items-center justify-between" data-id="element-2717">
                  <label className="flex items-center cursor-pointer" data-id="element-2718">
                    <input type="checkbox" className="h-4 w-4 border-gray-300 rounded cursor-pointer" style={{
                  accentColor: brand.primary
                }} data-id="element-2719" />

                    <span className="ml-2 text-sm" style={{
                  color: colors.text.secondary
                }} data-id="element-2720">
                      {t('pages.Login.remember_me')}
                    </span>
                  </label>
                  <button type="button" className="text-sm font-medium transition-colors" style={{
                color: colors.text.link
              }} onMouseEnter={e => e.currentTarget.style.color = colors.text.linkHover} onMouseLeave={e => e.currentTarget.style.color = colors.text.link} data-id="element-2721">
                    {t('pages.Login.forgot_password')}
                  </button>
                </div>

                <button type="submit" className="w-full font-medium py-3.5 px-4 rounded-lg transition-colors flex items-center justify-center shadow-sm" style={{
              backgroundColor: brand.primary,
              color: colors.neutral[0]
            }} onMouseEnter={e => e.currentTarget.style.backgroundColor = brand.primaryHover} onMouseLeave={e => e.currentTarget.style.backgroundColor = brand.primary} data-id="element-2722">
                  <LogInIcon size={18} className="mr-2" data-id="element-2723" />
                  {t('pages.Login.sign_in')}
                </button>
              </form>

              {/* Invitation entry point */}
              <div className="mt-8" data-id="element-2724">
                <div className="relative flex items-center" data-id="element-2725">
                  <div className="flex-grow border-t" style={{
                borderColor: colors.border.light
              }} data-id="element-2726" />
                  <span className="flex-shrink mx-3 text-xs uppercase tracking-wider" style={{
                color: colors.text.tertiary
              }} data-id="element-2727">
                    {t('pages.Login.or')}
                  </span>
                  <div className="flex-grow border-t" style={{
                borderColor: colors.border.light
              }} data-id="element-2728" />
                </div>

                <button type="button" onClick={() => {
              resetInvite();
              setStep('invite-code');
            }} className="mt-4 w-full font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center" style={{
              border: `1px solid ${colors.border.DEFAULT}`,
              color: colors.text.primary,
              backgroundColor: colors.neutral[0]
            }} onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = colors.neutral[50];
              e.currentTarget.style.borderColor = brand.primary;
            }} onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = colors.neutral[0];
              e.currentTarget.style.borderColor = colors.border.DEFAULT;
            }} data-id="element-2729">
                  <TicketIcon size={18} className="mr-2" style={{
                color: brand.primary
              }} data-id="element-2730" />
                  {t('pages.Login.received_an_invitation_code')}
                </button>
              </div>

              <div className="mt-8 text-center lg:text-left" data-id="element-2731">
                <p className="text-sm" style={{
              color: colors.text.secondary
            }} data-id="element-2732">
                  {t('pages.Login.dont_have_a_merchant_account')}{' '}
                  <button className="font-medium transition-colors" style={{
                color: colors.text.link
              }} onMouseEnter={e => e.currentTarget.style.color = colors.text.linkHover} onMouseLeave={e => e.currentTarget.style.color = colors.text.link} data-id="element-2733">
                    {t('pages.Login.contact_sales')}
                  </button>
                </p>
              </div>
            </>}

          {step === 'mfa' && <>
              <button type="button" onClick={() => {
            setStep('login');
            setMfaCode(['', '', '', '', '', '']);
          }} className="flex items-center text-sm mb-8 transition-colors" style={{
            color: colors.text.tertiary
          }} onMouseEnter={e => e.currentTarget.style.color = colors.neutral[700]} onMouseLeave={e => e.currentTarget.style.color = colors.text.tertiary} data-id="element-2734">
                <ArrowLeftIcon size={16} className="mr-1.5" data-id="element-2735" />
                {t('pages.Login.back_to_sign_in')}
              </button>

              <div className="text-center mb-8" data-id="element-2736">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{
              backgroundColor: brand.primaryLight
            }} data-id="element-2737">
                  <MailIcon size={28} style={{
                color: brand.primary
              }} data-id="element-2738" />
                </div>
                <h2 className="text-2xl font-bold mb-2" style={{
              color: colors.text.primary
            }} data-id="element-2739">
                  {t('pages.Login.check_your_email')}
                </h2>
                <p className="text-sm leading-relaxed" style={{
              color: colors.text.secondary
            }} data-id="element-2740">
                  {t('pages.Login.weve_sent_a_6digit_verification_code')}
                </p>
                <p className="font-medium text-sm mt-1" style={{
              color: colors.text.primary
            }} data-id="element-2741">
                  {getMaskedEmail()}
                </p>
              </div>

              <form onSubmit={handleMfaSubmit} className="space-y-6" data-id="element-2742">
                <div data-id="element-2743">
                  <label className="block text-sm font-medium mb-3 text-center" style={{
                color: colors.neutral[700]
              }} data-id="element-2744">
                    {t('pages.Login.enter_verification_code')}
                  </label>
                  <div className="flex justify-center gap-3" data-id="element-2745">
                    {mfaCode.map((digit, index) => <input key={index} ref={el => {
                  inputRefs.current[index] = el;
                }} type="text" inputMode="numeric" maxLength={6} value={digit} onChange={e => handleMfaInput(index, e.target.value)} onKeyDown={e => handleMfaKeyDown(index, e)} className="w-12 h-14 text-center text-xl font-semibold rounded-lg outline-none transition-all" style={{
                  border: `1px solid ${digit ? brand.primary : colors.border.DEFAULT}`,
                  backgroundColor: digit ? brand.primarySubtle : colors.neutral[50],
                  boxShadow: digit ? `0 0 0 1px ${brand.primaryRingActive}` : 'none'
                }} onFocus={e => {
                  e.target.select();
                  if (!digit) {
                    e.target.style.borderColor = brand.primary;
                    e.target.style.boxShadow = `0 0 0 2px ${brand.primaryRing}`;
                    e.target.style.backgroundColor = colors.neutral[0];
                  }
                }} onBlur={e => {
                  if (!digit) {
                    e.target.style.borderColor = colors.border.DEFAULT;
                    e.target.style.boxShadow = 'none';
                    e.target.style.backgroundColor = colors.neutral[50];
                  }
                }} data-id="element-2746" />)}
                  </div>
                </div>

                <button type="submit" disabled={!isCodeComplete} className="w-full font-medium py-3.5 px-4 rounded-lg transition-colors flex items-center justify-center shadow-sm" style={{
              backgroundColor: isCodeComplete ? brand.primary : colors.neutral[100],
              color: isCodeComplete ? colors.neutral[0] : colors.neutral[400],
              cursor: isCodeComplete ? 'pointer' : 'not-allowed'
            }} onMouseEnter={e => {
              if (isCodeComplete) e.currentTarget.style.backgroundColor = brand.primaryHover;
            }} onMouseLeave={e => {
              if (isCodeComplete) e.currentTarget.style.backgroundColor = brand.primary;
            }} data-id="element-2747">
                  <ShieldCheckIcon size={18} className="mr-2" data-id="element-2748" />
                  {t('pages.Login.verify_sign_in')}
                </button>
              </form>

              <div className="mt-6 text-center" data-id="element-2749">
                <p className="text-sm" style={{
              color: colors.text.tertiary
            }} data-id="element-2750">
                  {t('pages.Login.didnt_receive_the_code')}{' '}
                  {resendCountdown > 0 ? <span style={{
                color: colors.neutral[400]
              }} data-id="element-2751">
                      {t('pages.Login.resend_in')} {resendCountdown}
                      {t('pages.Login.s')}
                    </span> : <button type="button" onClick={handleResend} className="font-medium transition-colors" style={{
                color: colors.text.link
              }} onMouseEnter={e => e.currentTarget.style.color = colors.text.linkHover} onMouseLeave={e => e.currentTarget.style.color = colors.text.link} data-id="element-2752">
                      {t('pages.Login.resend_code')}
                    </button>}
                </p>
              </div>

              <div className="mt-4 p-3.5 rounded-lg" style={{
            backgroundColor: colors.neutral[50],
            border: `1px solid ${colors.border.light}`
          }} data-id="element-2753">
                <div className="flex items-start gap-2.5" data-id="element-2754">
                  <ShieldCheckIcon size={16} className="mt-0.5 flex-shrink-0" style={{
                color: colors.neutral[400]
              }} data-id="element-2755" />

                  <p className="text-xs leading-relaxed" style={{
                color: colors.text.tertiary
              }} data-id="element-2756">
                    {t('pages.Login.for_your_security_this_code_expires')}
                  </p>
                </div>
              </div>
            </>}

          {step === 'invite-code' && <>
              <button type="button" onClick={() => {
            resetInvite();
            setStep('login');
          }} className="flex items-center text-sm mb-8 transition-colors" style={{
            color: colors.text.tertiary
          }} onMouseEnter={e => e.currentTarget.style.color = colors.neutral[700]} onMouseLeave={e => e.currentTarget.style.color = colors.text.tertiary} data-id="element-2757">
                <ArrowLeftIcon size={16} className="mr-1.5" data-id="element-2758" />
                {t('pages.Login.back_to_sign_in')}
              </button>

              <div className="text-center mb-8" data-id="element-2759">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{
              backgroundColor: brand.primaryLight
            }} data-id="element-2760">
                  <TicketIcon size={28} style={{
                color: brand.primary
              }} data-id="element-2761" />
                </div>
                <h2 className="text-2xl font-bold mb-2" style={{
              color: colors.text.primary
            }} data-id="element-2762">
                  {t('pages.Login.invite_enter_code_title')}
                </h2>
                <p className="text-sm leading-relaxed" style={{
              color: colors.text.secondary
            }} data-id="element-2763">
                  {t('pages.Login.invite_enter_code_description')}
                </p>
              </div>

              <form onSubmit={handleInviteCodeSubmit} className="space-y-6" data-id="element-2764">
                <div data-id="element-2765">
                  <label className="block text-sm font-medium mb-2" style={{
                color: colors.neutral[700]
              }} data-id="element-2766">
                    {t('pages.Login.invitation_code')}
                  </label>
                  <input type="text" value={inviteCode} onChange={e => {
                // Auto-format to BML-XXXX-XXXX. Strip the BML prefix
                // and any non-alphanumerics, then re-insert dashes at
                // positions 4 and 8 of the body.
                const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                const body = (raw.startsWith('BML') ? raw.slice(3) : raw).slice(0, 8);
                let formatted = 'BML';
                if (body.length > 0) {
                  formatted += '-' + body.slice(0, 4);
                }
                if (body.length > 4) {
                  formatted += '-' + body.slice(4, 8);
                }
                setInviteCode(formatted === 'BML' ? '' : formatted);
                setInviteError('');
              }} placeholder="BML-XXXX-XXXX" maxLength={13} autoFocus className="w-full px-4 py-3 rounded-lg outline-none transition-colors font-mono tracking-wider" style={{
                border: `1px solid ${inviteError ? '#DC2626' : colors.border.DEFAULT}`,
                backgroundColor: colors.neutral[50]
              }} onFocus={e => {
                if (inviteError) return;
                e.target.style.borderColor = brand.primary;
                e.target.style.boxShadow = `0 0 0 2px ${brand.primaryRing}`;
                e.target.style.backgroundColor = colors.neutral[0];
              }} onBlur={e => {
                if (inviteError) return;
                e.target.style.borderColor = colors.border.DEFAULT;
                e.target.style.boxShadow = 'none';
                e.target.style.backgroundColor = colors.neutral[50];
              }} required data-id="element-2767" />
                  {inviteError && <p className="mt-2 text-sm" style={{
                color: '#DC2626'
              }} data-id="element-2768">
                      {inviteError}
                    </p>}
                </div>

                <button type="submit" disabled={inviteCode.trim().length === 0} className="w-full font-medium py-3.5 px-4 rounded-lg transition-colors flex items-center justify-center shadow-sm" style={{
              backgroundColor: inviteCode.trim().length > 0 ? brand.primary : colors.neutral[100],
              color: inviteCode.trim().length > 0 ? colors.neutral[0] : colors.neutral[400],
              cursor: inviteCode.trim().length > 0 ? 'pointer' : 'not-allowed'
            }} onMouseEnter={e => {
              if (inviteCode.trim().length > 0) e.currentTarget.style.backgroundColor = brand.primaryHover;
            }} onMouseLeave={e => {
              if (inviteCode.trim().length > 0) e.currentTarget.style.backgroundColor = brand.primary;
            }} data-id="element-2769">
                  {t('pages.Login.continue')}
                </button>
              </form>

              <div className="mt-8 p-3.5 rounded-lg" style={{
            backgroundColor: colors.neutral[50],
            border: `1px solid ${colors.border.light}`
          }} data-id="element-2770">
                <p className="text-xs leading-relaxed" style={{
              color: colors.text.tertiary
            }} data-id="element-2771">
                  {t('pages.Login.invite_help_text')}
                </p>
              </div>
            </>}

          {step === 'invite-setup' && <>
              <button type="button" onClick={() => setStep('invite-code')} className="flex items-center text-sm mb-8 transition-colors" style={{
            color: colors.text.tertiary
          }} onMouseEnter={e => e.currentTarget.style.color = colors.neutral[700]} onMouseLeave={e => e.currentTarget.style.color = colors.text.tertiary} data-id="element-2772">
                <ArrowLeftIcon size={16} className="mr-1.5" data-id="element-2773" />
                {t('pages.Login.back')}
              </button>

              <div className="mb-8" data-id="element-2774">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{
              backgroundColor: brand.primaryLight
            }} data-id="element-2775">
                  <UserPlusIcon size={24} style={{
                color: brand.primary
              }} data-id="element-2776" />
                </div>
                <h2 className="text-2xl font-bold mb-2" style={{
              color: colors.text.primary
            }} data-id="element-2777">
                  {t('pages.Login.invite_setup_title')}
                </h2>
                <p className="text-sm leading-relaxed" style={{
              color: colors.text.secondary
            }} data-id="element-2778">
                  {t('pages.Login.invite_setup_description')}
                </p>
              </div>

              <form onSubmit={handleInviteSetupSubmit} className="space-y-5" data-id="element-2779">
                <div data-id="element-2780">
                  <label className="block text-sm font-medium mb-2" style={{
                color: colors.neutral[700]
              }} data-id="element-2781">
                    {t('pages.Login.email_address')}
                  </label>
                  <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} readOnly className="w-full px-4 py-3 rounded-lg outline-none cursor-not-allowed" style={{
                border: `1px solid ${colors.border.DEFAULT}`,
                backgroundColor: colors.neutral[100],
                color: colors.text.secondary
              }} data-id="element-2782" />
                  <p className="mt-1.5 text-xs" style={{
                color: colors.text.tertiary
              }} data-id="element-2783">
                    {t('pages.Login.email_locked_from_invitation')}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-2784">
                  <div data-id="element-2785">
                    <label className="block text-sm font-medium mb-2" style={{
                  color: colors.neutral[700]
                }} data-id="element-2786">
                      {t('pages.Login.first_name')}
                    </label>
                    <input type="text" value={inviteFirstName} onChange={e => setInviteFirstName(e.target.value)} placeholder="Ahmed" autoFocus className="w-full px-4 py-3 rounded-lg outline-none transition-colors" style={inputBaseStyle} onFocus={e => {
                  e.target.style.borderColor = brand.primary;
                  e.target.style.boxShadow = `0 0 0 2px ${brand.primaryRing}`;
                  e.target.style.backgroundColor = colors.neutral[0];
                }} onBlur={e => {
                  e.target.style.borderColor = colors.border.DEFAULT;
                  e.target.style.boxShadow = 'none';
                  e.target.style.backgroundColor = colors.neutral[50];
                }} required data-id="element-2787" />
                  </div>
                  <div data-id="element-2788">
                    <label className="block text-sm font-medium mb-2" style={{
                  color: colors.neutral[700]
                }} data-id="element-2789">
                      {t('pages.Login.last_name')}
                    </label>
                    <input type="text" value={inviteLastName} onChange={e => setInviteLastName(e.target.value)} placeholder="Saleem" className="w-full px-4 py-3 rounded-lg outline-none transition-colors" style={inputBaseStyle} onFocus={e => {
                  e.target.style.borderColor = brand.primary;
                  e.target.style.boxShadow = `0 0 0 2px ${brand.primaryRing}`;
                  e.target.style.backgroundColor = colors.neutral[0];
                }} onBlur={e => {
                  e.target.style.borderColor = colors.border.DEFAULT;
                  e.target.style.boxShadow = 'none';
                  e.target.style.backgroundColor = colors.neutral[50];
                }} required data-id="element-2790" />
                  </div>
                </div>

                <div data-id="element-2791">
                  <label className="block text-sm font-medium mb-2" style={{
                color: colors.neutral[700]
              }} data-id="element-2792">
                    {t('pages.Login.create_password')}
                  </label>
                  <input type="password" value={invitePassword} onChange={e => setInvitePassword(e.target.value)} placeholder="••••••••" minLength={8} className="w-full px-4 py-3 rounded-lg outline-none transition-colors" style={inputBaseStyle} onFocus={e => {
                e.target.style.borderColor = brand.primary;
                e.target.style.boxShadow = `0 0 0 2px ${brand.primaryRing}`;
                e.target.style.backgroundColor = colors.neutral[0];
              }} onBlur={e => {
                e.target.style.borderColor = colors.border.DEFAULT;
                e.target.style.boxShadow = 'none';
                e.target.style.backgroundColor = colors.neutral[50];
              }} required data-id="element-2793" />
                  <p className="mt-1.5 text-xs" style={{
                color: colors.text.tertiary
              }} data-id="element-2794">
                    {t('pages.Login.password_requirements')}
                  </p>
                </div>

                <button type="submit" disabled={!isInviteSetupValid} className="w-full font-medium py-3.5 px-4 rounded-lg transition-colors flex items-center justify-center shadow-sm" style={{
              backgroundColor: isInviteSetupValid ? brand.primary : colors.neutral[100],
              color: isInviteSetupValid ? colors.neutral[0] : colors.neutral[400],
              cursor: isInviteSetupValid ? 'pointer' : 'not-allowed'
            }} onMouseEnter={e => {
              if (isInviteSetupValid) e.currentTarget.style.backgroundColor = brand.primaryHover;
            }} onMouseLeave={e => {
              if (isInviteSetupValid) e.currentTarget.style.backgroundColor = brand.primary;
            }} data-id="element-2795">
                  <UserPlusIcon size={18} className="mr-2" data-id="element-2796" />
                  {t('pages.Login.create_account')}
                </button>
              </form>
            </>}

          <div className="mt-16 pt-6 text-center lg:text-left" style={{
          borderTop: `1px solid ${colors.border.light}`
        }} data-id="element-2797">
            <p className="text-xs" style={{
            color: colors.text.tertiary
          }} data-id="element-2798">
              © 2026 {getTenant().brand.name}. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>;
}